var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { includeStyledSystem, sx } from "../../util/styledSystem";
import { Flex } from "../Flex";
import { Box } from "../Box";
import { Icon } from "../Icon";
const getVariants = ({ borders, colors: { alertBoxColors } }) => ({
    error: {
        backgroundColor: alertBoxColors.errorBackground,
        border: borders[1],
        borderColor: alertBoxColors.errorBorder,
        color: alertBoxColors.errorText,
    },
    info: {
        backgroundColor: alertBoxColors.infoBackground,
        border: borders[1],
        borderColor: alertBoxColors.infoBorder,
        color: alertBoxColors.infoText,
    },
    success: {
        backgroundColor: alertBoxColors.successBackground,
        border: borders[1],
        borderColor: alertBoxColors.successBorder,
        color: alertBoxColors.successText,
    },
    warning: {
        backgroundColor: alertBoxColors.warningBackground,
        border: borders[1],
        borderColor: alertBoxColors.warningBorder,
        color: alertBoxColors.warningText,
    },
});
const AlertBoxElement = styled(Flex) `
  box-sizing: border-box;
  min-width: 0;
  ${({ theme, variant }) => (Object.assign({}, getVariants(theme)[variant]))}
  ${includeStyledSystem()}
  ${sx}
`;
AlertBoxElement.defaultProps = {
    borderRadius: "4px",
    borderStyle: "solid",
    "data-testid": "alert",
    padding: 3,
    variant: "success",
};
const CloseButton = ({ onClick }) => (React.createElement(Box, { ml: 3, cursor: "pointer", onClick: onClick, "data-testid": "CloseButton" },
    React.createElement(Icon, { src: "Close", size: "12px" })));
const DISMISS_AFTER_DEFAULT = 2000;
/**
 * Alert Box is an alert that we trigger with a message for the user and a specific colour, also known as variant.
 * It can also be autodismissed, dismissed after some time, or have a close button.
 *
 * @example
 * <AlertBox variant="warning">Warning</AlertBox>
 */
export const AlertBox = (_a) => {
    var { autoClose = false, children, closeButton = false, dismissAfter = DISMISS_AFTER_DEFAULT, variant = "info", visible = true, setVisible } = _a, props = __rest(_a, ["autoClose", "children", "closeButton", "dismissAfter", "variant", "visible", "setVisible"]);
    const onClose = () => {
        setVisible === null || setVisible === void 0 ? void 0 : setVisible(false);
    };
    useEffect(() => {
        let timer;
        if (autoClose) {
            timer = setTimeout(() => setVisible === null || setVisible === void 0 ? void 0 : setVisible(false), dismissAfter);
        }
        return () => clearTimeout(timer);
    }, [autoClose, setVisible, dismissAfter]);
    return (React.createElement(AnimatePresence, null, visible && (React.createElement(motion.div, { exit: { opacity: 0, transition: { duration: 0.5 } } },
        React.createElement(AlertBoxElement, Object.assign({ variant: variant, role: "alert", "data-testid": "alertBoxElement" }, props),
            React.createElement(Flex, { flex: 1, justifyContent: "center", alignItems: "center" }, children),
            React.createElement(Flex, null, closeButton && React.createElement(CloseButton, { onClick: onClose })))))));
};
